﻿import Usercentrics from "../../Usercentrics/usercentrics";

// HACK Google Maps clusterer added from CDN, but I couldn't find typings for MarkerClusterer
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const markerClusterer: any;

interface Location {
  name: string;
  image: string;
  type: string;
  address: string;
  cityAndCountry: string;
  phone: string;
  coordinates: google.maps.LatLng;
  url: string;
}

let map: google.maps.Map | undefined;

const switchView = (view: "map" | "list") => {
  const mapView = document.getElementById("locations-map") as HTMLElement;
  const listView = document.getElementById("locations-list") as HTMLElement;

  const viewNameElement = document.getElementById("currentView") as HTMLInputElement;

  const sortOrderDropdown = document.getElementById("list-sort-options") as HTMLElement;

  if (view === "map") {
    mapView.classList.remove("hidden");
    listView.classList.add("hidden");
    sortOrderDropdown.classList.remove("md:block");
    viewNameElement.value = "map";
    if (map) {
      google.maps.event.trigger(map, "resize");
    }
  } else {
    sortOrderDropdown.classList.add("md:block");
    mapView.classList.add("hidden");
    listView.classList.remove("hidden");
    viewNameElement.value = "list";
  }
};

const initializeMapInternal = async (): Promise<void> => {
  const mapWrapper = document.getElementById("locations-map") as HTMLElement;

  if (!mapWrapper) {
    return;
  }

  // Check if user has consented to Google Maps, or if Usercentrics is disabled
  const googleMapsAllowed =
    !Usercentrics.enabled() || Usercentrics.getDataProcessingConsent("Google Maps"); // Name must exactly match that in Usercentrics

  if (!googleMapsAllowed) {
    console.warn("Missing consent for Google Maps");

    const mapWrapper = document.getElementById("locations-map") as HTMLElement;

    const consentWarning = mapWrapper.dataset.consentWarning;

    if (mapWrapper) {
      mapWrapper.innerHTML = `<div class="mt-20 border border-black p-20 bg-tint-orange">${consentWarning}</div>`;
    }

    return;
  }

  const locations = JSON.parse(mapWrapper.dataset.locations || "[]") as Location[];

  const { Map } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
  const { AdvancedMarkerElement } = (await google.maps.importLibrary(
    "marker"
  )) as google.maps.MarkerLibrary;

  const defaultStartPosition = { lat: 48.1236, lng: 16.2148 }; // Vienna

  let startPosition;

  if (locations?.length === 0) {
    // No locations, so we default to a standard starting point
    startPosition = defaultStartPosition;
  } else if (locations?.length === 1) {
    // Default to the only location being displayed
    startPosition = locations[0].coordinates;
  } else {
    // Multiple locations, so map will be fitted to cover all markers
    startPosition = null;
  }

  // The map, centered at start position
  map = new Map(mapWrapper, {
    mapId: "locations",

    zoom: 10, // Only applicable if there is zero or 1 locations since we otherwise fit the bounds to all markers
    center: startPosition,
    maxZoom: 15,

    // Prevent displaying (gray) areas outside of the map
    restriction: {
      latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
    },
  });

  const markers: Array<google.maps.marker.AdvancedMarkerElement> = [];

  // Currently open marker info window, if any
  let openInfoWindow: google.maps.InfoWindow | null = null;

  const closeInfoWindow = () => {
    if (openInfoWindow) {
      openInfoWindow.close();
      openInfoWindow = null;
    }
  };

  // Close info window when map is clicked
  google.maps.event.addListener(map, "click", () => {
    closeInfoWindow();
  });

  // Place markers
  locations?.forEach(location => {
    const markerImage = document.createElement("img");
    markerImage.src = "/dist/Features/Locations/Landing/map-pin.svg";

    // We need to separate locations on the same spot to make their markers at least somewhat clickable at maximum zoom.
    const duplicates = locations.filter(
      item =>
        location.coordinates.lat === item.coordinates.lat &&
        location.coordinates.lng === item.coordinates.lng &&
        location.url !== item.url // Not interested in current location.
    );

    if (duplicates && duplicates.length > 0) {
      // HACK Type definitions are incorrect, they define lat and lng as methods instead of numbers.
      const lat = location.coordinates.lat as unknown as number;
      const lng = (location.coordinates.lng as unknown as number) + 0.0001; // Move current location 11,1 meters to the east.

      location.coordinates = new google.maps.LatLng(lat, lng);
    }

    const marker = new AdvancedMarkerElement({
      position: location.coordinates,
      map: map,
      content: markerImage,
    });

    markers.push(marker);

    // Add info window for when marker is clicked
    const infoWindow = new google.maps.InfoWindow();

    // Position info window to the left of the marker
    infoWindow.setOptions({
      pixelOffset: new google.maps.Size(-200, 240),
    });

    marker.addListener("click", () => {
      // Close previously open info window, if any
      closeInfoWindow();

      const arrow = `
          <div class="flex-none flex items-center justify-center relative overflow-hidden mt-20 w-[46px] h-[46px] rounded-full bg-tint-silver group-hover:bg-tint-orange">
            <svg class="text-primary-orange absolute group-hover:animate-pacman-x" width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.67969 0.320312L13.3203 7L6.67969 13.6797L5.50781 12.5078L10.1562 7.82031H0V6.17969H10.1562L5.50781 1.49219L6.67969 0.320312Z" fill="currentColor"></path>
            </svg>
          </div>
      `;

      let content = `
        <div class="p-20 group relative">
          <span class="text-tag uppercase tracking-5 text-gray inline-block mb-5">${
            location.type
          }</span><br>
          <span class="text-h6 inline-block mb-10">${location.name}</span><br>
          <span class="text-paragraph-xs text-gray leading-140 font-light">
            ${location.address}<br>
            ${location.cityAndCountry}<br>
            ${location.phone}
          </span>
          ${location.url ? arrow : ""}
        </div>
      `;

      if (location.image) {
        content = `<img src="${location.image}" />${content}`;
      }

      infoWindow.setOptions({
        ariaLabel: location.name,
      });

      if (location.url) {
        infoWindow.setContent(
          `<a href="${location.url}" class="block w-[340px]">${content}</a>`
        );
      } else {
        infoWindow.setContent(`<div class="block w-[340px]">${content}</div>`);
      }

      infoWindow.open(map, marker);

      openInfoWindow = infoWindow;
    });
  });

  const getBounds = (markers: google.maps.marker.AdvancedMarkerElement[]) => {
    const bounds = new google.maps.LatLngBounds();

    markers.forEach(marker => {
      const position = marker.position;
      if (position) {
        bounds.extend(position);
      }
    });

    return bounds;
  };

  // Fit map to include all markers
  if (markers.length > 1) {
    const bounds = getBounds(markers);

    map.fitBounds(bounds, 10);
  }

  // Cluster markers using custom renderer for the cluster graphic
  const renderer = {
    render({
      count,
      position,
    }: {
      count: number;
      position: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined;
    }) {
      // We use inline SVG for cluster marker since we want to inject 'count' into the text node.
      const svg =
        '<svg width="51" height="50" viewBox="0 0 51 50" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<g filter="url(#filter0_d_2487_54692)">' +
        '<circle cx="25.002" cy="21" r="21" fill="white"/>' +
        `<text x="50%" y="50%" style="fill:#000" text-anchor="middle" font-size="14" font-weight="700" dominant-baseline="middle" font-family="MADE Evolve Sans">${count}</text>` +
        "</g>" +
        "<defs>" +
        '<filter id="filter0_d_2487_54692" x="0.00195312" y="0" width="50" height="50" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">' +
        '<feFlood flood-opacity="0" result="BackgroundImageFix"/>' +
        '<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>' +
        '<feOffset dy="4"/>' +
        '<feGaussianBlur stdDeviation="2"/>' +
        '<feComposite in2="hardAlpha" operator="out"/>' +
        '<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>' +
        '<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2487_54692"/>' +
        '<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2487_54692" result="shape"/>' +
        "</filter>" +
        "</defs>" +
        "</svg>";

      const parser = new DOMParser();
      const svgElement = parser.parseFromString(svg, "image/svg+xml").documentElement;

      // Adjust zIndex to be above other markers
      // 1 000 000 is the value of the obsolete MAX_ZINDEX marker constant used previously, does not exist for AdvancedMarkerElement.
      const zIndex = 1000000 + count;

      const clusterOptions = {
        map,
        position,
        zIndex,
        content: svgElement,
      };
      return new google.maps.marker.AdvancedMarkerElement(clusterOptions);
    },
  };

  new markerClusterer.MarkerClusterer({
    map,
    markers,
    renderer,
    algorithmOptions: { maxZoom: 14 }, // Do not cluster at maximum zoom, must be able to click markers.
    onClusterClick: function (
      _: google.maps.MapMouseEvent,
      cluster: {
        markers?: google.maps.marker.AdvancedMarkerElement[];
      },
      map: google.maps.Map
    ) {
      closeInfoWindow();

      const clusterMarkers: Array<google.maps.marker.AdvancedMarkerElement> =
        cluster.markers || [];

      if (!clusterMarkers?.length) {
        console.warn("Cluster har no markers");
        return;
      }

      const clusterBounds = getBounds(clusterMarkers);

      map.fitBounds(clusterBounds);

      map.setCenter(clusterBounds.getCenter());
    },
  });
};

export const initializeMap = async (ajaxRequest: boolean): Promise<void> => {
  const mapViewButton = document.getElementById("map-view-button") as HTMLButtonElement;

  const listViewButton = document.getElementById("list-view-button") as HTMLButtonElement;

  mapViewButton.addEventListener("click", _ => {
    mapViewButton.classList.add("bg-tint-silver");
    listViewButton.classList.remove("bg-tint-silver");
    switchView("map");
  });

  listViewButton.addEventListener("click", _ => {
    listViewButton.classList.add("bg-tint-silver");
    mapViewButton.classList.remove("bg-tint-silver");
    switchView("list");
  });

  // Hold off on Google Maps initialization until consent can be verified
  if (!ajaxRequest && Usercentrics.enabled()) {
    Usercentrics.addEventListener("initialized", initializeMapInternal);
    Usercentrics.addEventListener("updated", initializeMapInternal);
  } else {
    await initializeMapInternal();
  }
};
